import { useState } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} QuestItemType
 * @property {number} id - The quest ID
 * @property {string} title - Quest title
 * @property {string} type - Quest type (e.g., "quest", "festive")
 */

/**
 * @typedef {Object} QuestListResponse
 * @property {boolean} success - Whether the request was successful
 * @property {[string, QuestItemType[]]} message - Tuple containing success message and quest array
 * @property {null} data - Always null for this endpoint
 * @property {null} meta - Always null for this endpoint
 */

/**
 * Custom hook to fetch all quests for assigning to a school
 *
 * @returns {{
 *   quests: QuestItemType[] | undefined,
 *   loading: boolean,
 *   error: string,
 *   getQuestListForAssigning: () => Promise<void>
 * }}
 */
export const useGetQuestListForAssigning = () => {
    const [quests, setQuests] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { fetchWithAuth } = useAuth();
    const apiUrl = process.env.REACT_APP_API_URL;

    /**
     * Fetches all available quests for assigning to a school
     */
    const getQuestListForAssigning = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await fetchWithAuth(`${apiUrl}/school/quests`, {
                method: "GET",
            });

            if (!response.ok) throw new Error("Failed to fetch quests");

            /** @type {QuestListResponse} */
            const responseData = await response.json();

            setQuests(responseData?.message?.[1] || []);
        } catch (err) {
            setQuests(null);
            setError(err?.message || ERROR_MESSAGES.UNKNOWN);
        } finally {
            setLoading(false);
        }
    };

    return {
        quests,
        loading,
        error,
        getQuestListForAssigning,
    };
};
